// Shared chart primitives.
//
// AxisChart / MetricChart / CoverageChart are extracted from
// screens/pipeline.tsx (unchanged behavior — pipeline re-exports them), now
// fully typed. StackedMetricChart is the Data Pipeline HISTORY bar chart
// (design-bundle history.js DPBars port): stacked series, axis gutter,
// gridlines, accented+labeled peak, dashed range-average line, retention-gap
// columns, and a fixed-position tooltip per bucket.

import { useEffect, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { fmt } from '@/components/primitives';
import { CPSparkBtn } from '@/components/copilot';
import { fmtHM, fmtDayShort, nyParts } from '@/lib/datetime';

// ── shared helpers ─────────────────────────────────────────────────────────

/** Round a peak up to a "nice" axis maximum (1 / 2 / 2.5 / 5 ×10ⁿ). */
export function niceMax(peak: number): number {
  if (!(peak > 0)) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(peak)));
  const n = peak / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return step * pow;
}

function pctColor(v: number, { good = 80, warn = 50 } = {}): string {
  if (v >= good) return 'var(--good)';
  if (v >= warn) return 'var(--warn)';
  return 'var(--bad)';
}

/** Abbreviated axis count (mock DP.k): 1.2k / 24k / 1.02M, rounded below 1k. */
function kFmt(v: number): string {
  return v >= 1e6
    ? (v / 1e6).toFixed(2) + 'M'
    : v >= 1e4
      ? Math.round(v / 1e3) + 'k'
      : v >= 1e3
        ? (v / 1e3).toFixed(1) + 'k'
        : String(Math.round(v));
}

function nFmt(v: number): string {
  return Math.round(v).toLocaleString('en-US');
}

/** Bucket ISO → NY axis label ("14:00" for hour grain, "Jul 7" for day grain). */
export function bucketLabel(iso: string, bucket: 'hour' | 'day'): string {
  if (bucket === 'hour') return fmtHM(iso);
  return fmtDayShort(iso);
}

// ── AxisChart (pipeline.tsx extraction) ────────────────────────────────────
// Shared chart scaffold: a fixed left gutter (crisp HTML axis labels, aligned
// to the gridline fractions) + a full-width SVG plot. The SVG viewBox height
// equals the pixel height `h`, so the vertical axis is 1:1 and gridlines/
// labels line up. `ticks` = [{ frac, label }] from top (frac 1) to bottom
// (frac 0). `render(geom)` draws the bars; `overlay(geom)` adds HTML
// positioned over the plot (peak label).

const GUTTER = 38; // px

export interface AxisTick {
  frac: number;
  label: string;
}

export interface AxisGeom {
  w: number;
  h: number;
  pt: number;
  pb: number;
  plotH: number;
  yFor: (frac: number) => number;
}

export function AxisChart({ h = 88, ticks, render, overlay }: {
  h?: number;
  ticks: AxisTick[];
  render: (geom: AxisGeom) => ReactNode;
  overlay?: ((geom: AxisGeom) => ReactNode) | null;
}) {
  const w = 520;                 // viewBox width; bars stretch to fill horizontally
  const pt = 8, pb = 3;          // top/bottom insets (px == viewBox units, h is 1:1)
  const plotH = h - pt - pb;
  const yFor = (frac: number) => pt + (1 - Math.max(0, Math.min(1, frac))) * plotH; // frac 0..1 → y
  const geom: AxisGeom = { w, h, pt, pb, plotH, yFor };
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <div style={{ position: 'relative', width: GUTTER, height: h, flexShrink: 0 }}>
        {ticks.map((t, i) => (
          <span key={i} style={{
            position: 'absolute', right: 4, top: yFor(t.frac), transform: 'translateY(-50%)',
            fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.04em', color: 'var(--ink-3)', whiteSpace: 'nowrap',
          }}>{t.label}</span>
        ))}
      </div>
      <div style={{ position: 'relative', flex: 1, height: h, minWidth: 0 }}>
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
          <g className="chart-grid">
            {ticks.map((t, i) => (
              <line key={i} x1={0} x2={w} y1={yFor(t.frac)} y2={yFor(t.frac)} vectorEffect="non-scaling-stroke"/>
            ))}
          </g>
          {render(geom)}
        </svg>
        {overlay && overlay(geom)}
      </div>
    </div>
  );
}

// ── MetricChart (pipeline.tsx extraction) ──────────────────────────────────
// Daily magnitude series (tickets / revenue). Auto-scaled to a rounded Y-max,
// with the single peak bar accented + labeled with its exact value.

export function MetricChart({ data, dayLabels, fmtTip, fmtAxis, fmtPeak, highlightZero = false, h = 88 }: {
  data: ReadonlyArray<number>;
  dayLabels: ReadonlyArray<string>;
  fmtTip: (v: number) => string;
  fmtAxis: (v: number) => string;
  fmtPeak: (v: number) => string;
  highlightZero?: boolean;
  h?: number;
}) {
  if (!data || !data.length) return <span className="muted">—</span>;
  const vals = data.map((v) => Math.max(Number(v) || 0, 0));
  const peak = Math.max(...vals, 0);
  const top = niceMax(peak);
  const peakIdx = peak > 0 ? vals.indexOf(peak) : -1;
  const ticks: AxisTick[] = [
    { frac: 1, label: fmtAxis(top) },
    { frac: 0.5, label: fmtAxis(top / 2) },
    { frac: 0, label: '0' },
  ];
  const render = ({ w, yFor, h: H, pb }: AxisGeom) => {
    const bw = w / data.length;
    return vals.map((val, i) => {
      const zero = val === 0;
      const y = zero ? H - pb - 2 : yFor(val / top);
      const shown = Math.max(H - pb - y, zero ? 2 : 1);
      const isPeak = i === peakIdx && peak > 0;
      return (
        <rect key={i} x={i * bw} y={y} width={Math.max(bw - 1, 1)} height={shown}
          fill={zero && highlightZero ? 'var(--bad)' : isPeak ? 'var(--accent)' : 'var(--ink-2)'}
          opacity={zero && highlightZero ? 0.5 : isPeak ? 0.95 : 0.85}>
          <title>{`${dayLabels[i] ?? ''} · ${fmtTip(val)}`}</title>
        </rect>
      );
    });
  };
  const overlay = peak > 0 ? ({ yFor }: AxisGeom) => {
    const leftPct = ((peakIdx + 0.5) / data.length) * 100;
    const top2 = Math.max(yFor(1) - 2, 1); // clamp inside the plot
    return (
      <span style={{
        position: 'absolute', left: `${leftPct}%`, top: top2, transform: 'translate(-50%, -100%)',
        fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700, color: 'var(--accent)', whiteSpace: 'nowrap', pointerEvents: 'none',
      }}>{fmtPeak(peak)}</span>
    );
  } : null;
  return <AxisChart h={h} ticks={ticks} render={render} overlay={overlay}/>;
}

// ── CoverageChart (pipeline.tsx extraction) ────────────────────────────────
// Daily coverage series, fixed 0–100% with a dashed house-average line + tag.
// Bars are colored by coverage; days with no tickets render as faint gaps.

export function CoverageChart({ data, dayLabels, counts, totals, avg, h = 88 }: {
  data: ReadonlyArray<number>;
  dayLabels: ReadonlyArray<string>;
  counts: ReadonlyArray<number>;
  totals: ReadonlyArray<number>;
  avg?: number | null;
  h?: number;
}) {
  if (!data || !data.length) return <span className="muted">—</span>;
  const ticks: AxisTick[] = [
    { frac: 1, label: '100%' },
    { frac: 0.5, label: '50%' },
    { frac: 0, label: '0%' },
  ];
  const render = ({ w, yFor, h: H, pb }: AxisGeom) => {
    const bw = w / data.length;
    return data.map((v, i) => {
      const hasData = (totals[i] ?? 0) > 0;
      const val = Math.max(Number(v) || 0, 0);
      const y = hasData ? yFor(val / 100) : H - pb - 2;
      const shown = Math.max(H - pb - y, hasData ? 1 : 2);
      return (
        <rect key={i} x={i * bw} y={y} width={Math.max(bw - 1, 1)} height={shown}
          fill={hasData ? pctColor(val) : 'var(--ink-3)'} opacity={hasData ? 0.85 : 0.25}>
          <title>{`${dayLabels[i] ?? ''} · ${hasData ? `${fmt.n(counts[i] ?? 0)}/${fmt.n(totals[i] ?? 0)} · ${Math.round(val)}%` : 'no tickets'}`}</title>
        </rect>
      );
    });
  };
  const renderAvg = ({ w, yFor }: AxisGeom) => (avg != null
    ? <line x1={0} x2={w} y1={yFor(avg / 100)} y2={yFor(avg / 100)} stroke="var(--ink-1)" strokeWidth={1} strokeDasharray="4 3" opacity={0.75} vectorEffect="non-scaling-stroke"/>
    : null);
  const overlay = avg != null ? ({ yFor }: AxisGeom) => (
    <span style={{
      position: 'absolute', right: 2, top: Math.max(yFor(avg / 100), 6), transform: 'translateY(-50%)',
      fontFamily: 'var(--font-mono)', fontSize: 8.5, fontWeight: 600, color: 'var(--ink-1)',
      background: 'color-mix(in oklch, var(--bg-elev) 80%, transparent)', padding: '0 3px', borderRadius: 2, pointerEvents: 'none',
    }}>avg {Math.round(avg)}%</span>
  ) : null;
  return <AxisChart h={h} ticks={ticks} render={(g) => <>{render(g)}{renderAvg(g)}</>} overlay={overlay}/>;
}

// ── StackedMetricChart (Data Pipeline history, design DPBars port) ─────────

// Fixed-position tooltip viewport clamps. Width tracks the tip's rendered
// footprint; height covers header + a few series rows + the Explain action
// row, so the tooltip (and its now-clickable bottom edge) never renders off
// the bottom of the viewport. The clamp path is shared: read-only tooltips
// (no onExplain) get the same harmless clamping.
const TIP_MAX_W = 190;
const TIP_MAX_H = 150;

export interface StackedSeries {
  name: string;
  color: string;
  /** `null` is a gap: nothing to measure in that bucket (a rate over no events). */
  values: ReadonlyArray<number | null>;
}

interface StackedTip {
  x: number;
  y: number;
  i: number;
}

export function StackedMetricChart({
  w: wFallback = 760, h = 200, buckets, bucket, series,
  yMax = null, avg = null, showPeak = true,
  gaps = null, gapNote = 'RETENTION LIMIT', unit = '',
  partial = null, partialNote = 'PARTLY DELETED',
  onExplain = null,
}: {
  /** The drawing width before the container is measured (and where it cannot be). */
  w?: number;
  h?: number;
  buckets: ReadonlyArray<string>;
  bucket: 'hour' | 'day';
  series: ReadonlyArray<StackedSeries>;
  /** Fixed axis max (100 renders % labels); null auto-scales with headroom. */
  yMax?: number | null;
  /** Dashed range-average line value. */
  avg?: number | null;
  showPeak?: boolean;
  /** Per-bucket retention gaps — grayed columns, excluded from the peak. */
  gaps?: ReadonlyArray<boolean> | null;
  gapNote?: string;
  unit?: string;
  /**
   * Per-bucket PARTIAL data — the retention floor falls inside the bucket, so
   * its counts are short. Drawn (outlined, hatched backdrop) and labelled in
   * the tooltip, distinct from a `gaps` column, and excluded from the peak.
   */
  partial?: ReadonlyArray<boolean> | null;
  partialNote?: string;
  /**
   * Copilot chart-bucket anchor (design §2a). When set, the tooltip gains a
   * bottom action row with the sparkle Explain button and survives the
   * pointer travelling into it (hide on a 160ms delay instead of instantly).
   * Receives the hovered bucket index. Null ⇒ the pre-existing tooltip,
   * byte-identical behavior.
   */
  onExplain?: ((i: number) => void) | null;
}) {
  const [tip, setTip] = useState<StackedTip | null>(null);
  const [inspectionIndex, setInspectionIndex] = useState(0);
  // Draw at the container's own width, so the axis text keeps its size in a
  // narrow column instead of shrinking with a fixed 760-unit drawing.
  const boxRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<number | null>(null);
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry?.contentRect.width ?? 0);
      setMeasured(width >= 200 ? width : null);
    });
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const w = measured ?? wFallback;
  const pad = { t: 18, r: 12, b: 22, l: 44 };
  // Hoverable-tooltip plumbing: with an Explain action the tip must not
  // vanish while the pointer crosses the gap into it.
  const hideT = useRef<number | null>(null);
  const cancelHide = () => {
    if (hideT.current != null) {
      window.clearTimeout(hideT.current);
      hideT.current = null;
    }
  };
  const scheduleHide = () => {
    if (!onExplain) { setTip(null); return; }
    cancelHide();
    hideT.current = window.setTimeout(() => setTip(null), 160);
  };
  useEffect(() => cancelHide, []);
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;
  const totals = buckets.map((_, i) => series.reduce((s, sr) => s + (sr.values[i] ?? 0), 0));
  // The axis covers every bar that is drawn, a partial bucket included, so no
  // bar overflows the chart. Only the peak marker skips partial buckets: a
  // bucket missing data is not a peak to compare against.
  const drawnTotals = totals.filter((_, i) => !gaps || !gaps[i]);
  const liveTotals = totals.filter((_, i) => (!gaps || !gaps[i]) && (!partial || !partial[i]));
  const max = yMax ?? Math.max(...(drawnTotals.length ? drawnTotals : [0]), 1) * 1.12;
  const slot = buckets.length ? iw / buckets.length : iw;
  const bw = Math.min(slot * 0.64, 26);
  const y = (v: number) => pad.t + ih - (v / max) * ih;
  const peakVal = liveTotals.length ? Math.max(...liveTotals) : 0;
  const peakIdx = peakVal > 0
    ? totals.findIndex((t, i) => t === peakVal && (!gaps || !gaps[i]) && (!partial || !partial[i]))
    : -1;
  const valText = (v: number | null | undefined) =>
    v == null ? '—' : (yMax === 100 ? Math.round(v) + '%' : nFmt(v)) + unit;
  const yticks = 3;
  const xEvery = Math.max(1, Math.ceil(buckets.length / Math.max(2, Math.floor(w / 95))));
  const fmtVal = (v: number) => (yMax === 100 ? Math.round(v) + '%' : kFmt(v));
  const move = (e: ReactMouseEvent, i: number) => {
    setInspectionIndex(i);
    setTip({ x: e.clientX, y: e.clientY, i });
  };
  const selected = Math.min(inspectionIndex, Math.max(0, buckets.length - 1));
  const inspect = (svg: SVGSVGElement, index: number) => {
    if (!buckets.length) return;
    const i = Math.max(0, Math.min(index, buckets.length - 1));
    const rect = svg.getBoundingClientRect();
    cancelHide();
    setInspectionIndex(i);
    setTip({ x: rect.left + (pad.l + slot * (i + .5)) / w * rect.width, y: rect.top + rect.height / 2, i });
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <svg className="chart" viewBox={`0 0 ${w} ${h}`} width="100%" style={{ height: 'auto' }}
           role={buckets.length ? 'slider' : 'img'} tabIndex={buckets.length ? 0 : undefined}
           aria-label="Chart values. Use Left and Right arrow keys to inspect."
           aria-valuemin={buckets.length ? 0 : undefined} aria-valuemax={buckets.length ? buckets.length - 1 : undefined}
           aria-valuenow={buckets.length ? selected : undefined}
           aria-valuetext={buckets[selected] ? `${bucketLabel(buckets[selected]!, bucket)}: ${gaps?.[selected] ? gapNote : (partial?.[selected] ? partialNote + ', ' : '') + series.map(sr => `${sr.name} ${valText(sr.values[selected])}`).join(', ')}` : undefined}
           onPointerDown={(event) => {
             const rect = event.currentTarget.getBoundingClientRect();
             // The whole plot is the touch target, even when individual bins
             // are narrower than one physical pixel on a phone.
             inspect(event.currentTarget, Math.floor(((event.clientX - rect.left) / rect.width * w - pad.l) / slot));
           }}
           onFocus={(event) => inspect(event.currentTarget, selected)}
           onKeyDown={(event) => {
             const index = event.key === 'ArrowRight' || event.key === 'ArrowUp' ? selected + 1
               : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? selected - 1
               : event.key === 'Home' ? 0 : event.key === 'End' ? buckets.length - 1
               : event.key === 'Enter' || event.key === ' ' ? selected : null;
             if (index !== null) { event.preventDefault(); inspect(event.currentTarget, index); }
             if (event.key === 'Escape') { cancelHide(); setTip(null); }
           }}
           onMouseLeave={scheduleHide}>
        <g className="chart-grid">
          {Array.from({ length: yticks + 1 }, (_, i) => (
            <line key={i} x1={pad.l} x2={w - pad.r} y1={pad.t + (ih * i) / yticks} y2={pad.t + (ih * i) / yticks}/>
          ))}
        </g>
        <g className="chart-axis">
          {Array.from({ length: yticks + 1 }, (_, i) => (
            <text key={i} x={pad.l - 7} y={pad.t + (ih * i) / yticks + 3} textAnchor="end">
              {fmtVal(max * (1 - i / yticks))}
            </text>
          ))}
          {buckets.map((b, i) => (i % xEvery === 0
            ? <text key={b} x={pad.l + slot * i + slot / 2} y={h - 7} textAnchor="middle">{bucketLabel(b, bucket)}</text>
            : null))}
        </g>
        {buckets.map((b, i) => {
          const x = pad.l + slot * i + (slot - bw) / 2;
          if (gaps && gaps[i]) {
            return <rect key={b} x={x} y={pad.t} width={bw} height={ih} fill="var(--ink-3)" opacity="0.12"/>;
          }
          let acc = 0;
          return (
            <g key={b}>
              {partial && partial[i] && (
                <rect x={x} y={pad.t} width={bw} height={ih} fill="var(--warn)" opacity="0.10"
                  stroke="var(--warn)" strokeOpacity="0.6" strokeDasharray="2 2" strokeWidth="1"/>
              )}
              {series.map((sr, si) => {
                const v = sr.values[i] ?? 0;
                const y1 = y(acc + v);
                const y0 = y(acc);
                acc += v;
                return v > 0 ? (
                  <rect key={si} x={x} y={y1} width={bw} height={Math.max(0.5, y0 - y1)}
                    fill={i === peakIdx && showPeak && si === 0 && series.length === 1 ? 'var(--accent)' : sr.color}
                    opacity={i === peakIdx && showPeak ? 1 : 0.88} rx="1"/>
                ) : null;
              })}
            </g>
          );
        })}
        {showPeak && peakIdx >= 0 && (totals[peakIdx] ?? 0) > 0 && (
          <text x={pad.l + slot * peakIdx + slot / 2} y={y(totals[peakIdx]!) - 5} textAnchor="middle"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fill: 'var(--accent)', fontWeight: 600 }}>
            {fmtVal(totals[peakIdx]!)}
          </text>
        )}
        {avg != null && (
          <g>
            <line x1={pad.l} x2={w - pad.r} y1={y(avg)} y2={y(avg)} stroke="var(--ink-2)" strokeWidth="1" strokeDasharray="4 4" opacity="0.75"/>
            <text x={w - pad.r - 2} y={y(avg) - 4} textAnchor="end" style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, fill: 'var(--ink-2)' }}>
              AVG {fmtVal(avg)}
            </text>
          </g>
        )}
        {/* hover hit columns */}
        {buckets.map((b, i) => (
          <rect key={'h' + b} x={pad.l + slot * i} y={pad.t} width={slot} height={ih} fill="transparent"
                onMouseEnter={(e) => { cancelHide(); move(e, i); }}
                onMouseMove={(e) => move(e, i)}/>
        ))}
      </svg>
      {tip && buckets[tip.i] != null && (
        <div className={'dp-chart-tip' + (onExplain ? ' cp-live' : '')}
             onMouseEnter={onExplain ? cancelHide : undefined}
             onMouseLeave={onExplain ? scheduleHide : undefined}
             style={{
               left: Math.max(12, Math.min(tip.x + 12, window.innerWidth - TIP_MAX_W)),
               top: Math.min(tip.y + 12, window.innerHeight - TIP_MAX_H),
             }}>
          <div className="hd">
            {bucketLabel(buckets[tip.i]!, bucket)}
            {bucket === 'day' ? ` · ${nyParts(buckets[tip.i]!).y}` : ''}
          </div>
          {gaps && gaps[tip.i] ? (
            <div className="r"><span className="k">{gapNote}</span><span className="v">no data</span></div>
          ) : (
            <>
              {partial && partial[tip.i] && (
                <div className="r"><span className="k">{partialNote}</span><span className="v">counts short</span></div>
              )}
              {series.map((sr, si) => (
                <div className="r" key={si}>
                  <span className="k">{sr.name}</span>
                  <span className="v">{valText(sr.values[tip.i])}</span>
                </div>
              ))}
              {series.length > 1 && (
                <div className="r"><span className="k">total</span><span className="v">{nFmt(totals[tip.i] ?? 0)}</span></div>
              )}
            </>
          )}
          {onExplain && !(gaps && gaps[tip.i]) && (
            <div className="cp-tip-action">
              <CPSparkBtn label="Explain" onClick={() => { const i = tip.i; setTip(null); onExplain(i); }}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
